import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowDown,
  FaCalendarCheck,
  FaClipboardList,
  FaMedal,
  FaPlay,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";
import { MdSwipe } from "react-icons/md";

import HeroActionButton from "../../components/ui/HeroActionButton";
import HeroStatCard from "../../components/ui/HeroStatCard";

import {
  buildHeaderSlides,
  getActiveSlide,
  sanitizeHtml,
} from "./utils/homeUtils";

const SLIDE_DELAY = 9000;

const HeaderSlider = ({ settings, loading = false }) => {
  const slides = useMemo(() => buildHeaderSlides(settings), [settings]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const activeSlide = getActiveSlide(slides, currentIndex);

  useEffect(() => {
    if (!slides.length || paused) return undefined;

    const timeout = setTimeout(() => {
      setCurrentIndex((index) => (index + 1) % slides.length);
    }, SLIDE_DELAY);

    return () => clearTimeout(timeout);
  }, [slides.length, currentIndex, paused]);

  useEffect(() => {
    if (currentIndex > slides.length - 1) {
      setCurrentIndex(0);
    }
  }, [currentIndex, slides.length]);

  const handleScrollDown = () => {
    const target = document.getElementById("description");

    if (!target) {
      window.scrollTo({
        top: window.innerHeight - 100,
        behavior: "smooth",
      });
      return;
    }

    const y = target.getBoundingClientRect().top + window.pageYOffset - 120;

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  };

  if (loading && !slides.length) {
    return (
      <header className="relative flex min-h-[calc(100dvh-48px)] w-full max-w-full items-center justify-center overflow-x-clip overflow-y-hidden bg-slate-950 pt-28 text-white">
        <div className="h-14 w-14 animate-spin rounded-full border-4 border-white/20 border-t-orange-400" />
      </header>
    );
  }

  return (
    <header
      className="relative isolate min-h-[calc(100dvh-48px)] w-full max-w-full overflow-x-clip overflow-y-hidden bg-slate-950 pt-28 text-white sm:pt-32"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0 w-full max-w-full overflow-hidden">
        {slides.map((slide, index) => {
          const active = index === currentIndex;

          return (
            <div
              key={`${slide.src}-${index}`}
              className={`absolute inset-0 w-full max-w-full overflow-hidden transition-opacity duration-1000 ${
                active ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={slide.src}
                alt={slide.alt}
                className={`h-full w-full max-w-full object-cover transition-transform duration-[9000ms] ${
                  active ? "scale-105" : "scale-100"
                }`}
              />

              <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/75 to-slate-950/35" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(249,115,22,0.25),transparent_28%),radial-gradient(circle_at_80%_20%,rgba(37,99,235,0.18),transparent_30%)]" />
            </div>
          );
        })}
      </div>

      <div className="relative z-10 mx-auto flex min-h-[calc(100dvh-160px)] w-full max-w-[1380px] items-center px-3 pb-24 min-[380px]:px-4 sm:px-6 lg:px-8">
        <div className="w-full min-w-0 max-w-5xl overflow-x-clip">
          <div className="mb-4 inline-flex max-w-full items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 text-[10px] font-bold uppercase leading-snug tracking-[0.12em] text-orange-200 shadow-lg shadow-black/10 backdrop-blur sm:mb-5 sm:text-xs sm:tracking-[0.18em]">
            <FaShieldAlt className="h-3.5 w-3.5 shrink-0" />
            <span className="min-w-0 break-words">
              Solution moderne pour vos compétitions
            </span>
          </div>

          <h1 className="max-w-full break-words text-[2rem] font-bold leading-[1.05] tracking-tight text-white min-[380px]:text-[2.35rem] sm:text-5xl lg:max-w-5xl lg:text-7xl">
            Organisez vos tournois avec une application claire, rapide et pensée
            terrain.
          </h1>

          <div
            className="mt-5 max-w-full break-words text-sm leading-relaxed text-slate-200 sm:max-w-3xl sm:text-lg [&_*]:max-w-full [&_*]:break-words"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                activeSlide?.text ||
                  "Préparez vos compétitions, gérez vos catégories, vos tableaux, vos poules, vos résultats publics et vos podiums depuis une seule interface."
              ),
            }}
          />

          <div className="mt-7 flex w-full max-w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <div className="w-full min-w-0 sm:w-auto">
              <HeroActionButton to="/competition" icon={FaPlay}>
                Créer / gérer un tournoi
              </HeroActionButton>
            </div>

            <div className="w-full min-w-0 sm:w-auto">
              <HeroActionButton
                to="/resultats-public"
                icon={FaMedal}
                variant="secondary"
              >
                Voir les résultats publics
              </HeroActionButton>
            </div>
          </div>

          <div className="mt-8 grid w-full max-w-4xl grid-cols-1 gap-3 sm:grid-cols-3">
            <HeroStatCard
              icon={FaCalendarCheck}
              value="Tournois"
              label="Préparation des compétitions, pesées & répartitions"
            />

            <HeroStatCard
              icon={FaClipboardList}
              value="Poules"
              label="Tableaux, poules, repêchages et classements"
            />

            <HeroStatCard
              icon={FaUsers}
              value="Clubs"
              label="Compétiteurs, résultats publics et podiums"
            />
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-1/2 z-20 flex max-w-[calc(100vw-1rem)] -translate-x-1/2 flex-col items-center gap-3 sm:bottom-5">
        {slides.length > 1 && (
          <div className="flex max-w-full items-center gap-2 overflow-hidden rounded-full border border-white/15 bg-white/10 px-3 py-2 backdrop-blur">
            {slides.map((slide, index) => (
              <button
                key={`${slide.src}-dot-${index}`}
                type="button"
                onClick={() => setCurrentIndex(index)}
                className={`h-2.5 shrink-0 rounded-full transition-all ${
                  index === currentIndex
                    ? "w-8 bg-orange-400"
                    : "w-2.5 bg-white/50 hover:bg-white"
                }`}
                aria-label={`Afficher le visuel ${index + 1}`}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={handleScrollDown}
          className="group inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-white shadow-xl backdrop-blur transition hover:-translate-y-1 hover:bg-orange-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 sm:h-12 sm:w-12"
          aria-label="Descendre vers la présentation"
        >
          <FaArrowDown className="h-4 w-4 transition group-hover:translate-y-0.5" />
        </button>
      </div>

      <div className="absolute bottom-5 right-4 z-20 hidden max-w-[calc(100vw-2rem)] items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 text-xs text-slate-200 backdrop-blur sm:flex">
        <MdSwipe className="h-4 w-4 shrink-0 text-orange-300" />
        <span className="truncate">Navigation fluide</span>
      </div>
    </header>
  );
};

export default HeaderSlider;